import { Stack, Row, Col, SubHeading } from '@workramp/ui';
import { RichTextContent } from '~/features/rich-text-editor';
import { I18n } from '~/common/explicit-exports';
import { StyledStack, AdvancedSearchContainer, AnswerSection, SourcesSection } from './LearnerAIQuestionAnswer.styles';
import { LearnerAIQuestionAnswerSkeleton, AIAnswerHeader, SourcesList, type Source } from './components';

interface LearnerAIQuestionAnswerProps {
  loading?: boolean;
  error?: string | null;
  answer?: string | null;
  content?: Source[];
  variant?: 'dropdown' | 'advanced';
  progressMessage?: string | null;
  isAcademy?: boolean;
}

/** @knipignore */
export const LearnerAIQuestionAnswer = ({
  loading = false,
  error = null,
  answer = null,
  content = [],
  variant = 'dropdown',
  progressMessage = null,
  isAcademy,
}: LearnerAIQuestionAnswerProps) => {
  const SOURCES_TITLE = I18n.t('js.learner.ai_search.sources_title');
  const isDropdown = variant === 'dropdown';

  if (loading) {
    if (!isDropdown) {
      return (
        <AdvancedSearchContainer>
          <LearnerAIQuestionAnswerSkeleton progressMessage={progressMessage} />
        </AdvancedSearchContainer>
      );
    }

    return <LearnerAIQuestionAnswerSkeleton progressMessage={progressMessage} />;
  }

  if (error) {
    return <StyledStack size={4}>{error}</StyledStack>;
  }

  if (!answer) {
    return null;
  }

  // Dropdown variant - compact layout
  if (isDropdown) {
    return (
      <StyledStack size={2}>
        <RichTextContent>{answer}</RichTextContent>
        <SourcesList sources={content} variant="dropdown" isAcademy={isAcademy} />
      </StyledStack>
    );
  }

  // Advanced search variant - side-by-side layout
  return (
    <AdvancedSearchContainer>
      <Stack size={4}>
        <Row align="start">
          <Col xs={content.length > 0 ? 8 : 12}>
            <Stack size={4}>
              <AIAnswerHeader answer={answer} />
              <AnswerSection>
                <RichTextContent>{answer}</RichTextContent>
              </AnswerSection>
            </Stack>
          </Col>
          {content.length > 0 && (
            <Col xs={4}>
              <SourcesSection>
                <Stack size={3}>
                  <SubHeading level="md">{SOURCES_TITLE}</SubHeading>
                  <SourcesList sources={content} variant="advanced" showTitle={false} isAcademy={isAcademy} />
                </Stack>
              </SourcesSection>
            </Col>
          )}
        </Row>
      </Stack>
    </AdvancedSearchContainer>
  );
};
